/**
 * Dialog hook for HTML5 <dialog> element integration with Phoenix LiveView
 *
 * Handles opening/closing the dialog based on LiveView state changes,
 * and syncs close events back to the server.
 */
import { captureException } from '../sentry.js'

export default {
  mounted () {
    this.dialogDisposed = false
    // Set up event listeners
    this.setupEventListeners()

    // Open dialog if initial state is true
    const shouldShow = this.el.dataset.show === 'true'
    if (shouldShow && !this.el.open) {
      this.openDialog()
    }
  },

  updated () {
    // React to LiveView state changes
    const shouldShow = this.el.dataset.show === 'true'
    const isOpen = this.el.open

    if (shouldShow && !isOpen) {
      this.openDialog()
    } else if (!shouldShow && isOpen) {
      this.closeDialog()
    }
  },

  setupEventListeners () {
    // Listen for native close event
    this.closeHandler = (e) => {
      this.handleClose(e)
    }
    this.el.addEventListener('close', this.closeHandler)

    // Listen for native cancel event (Escape key)
    this.cancelHandler = (e) => {
      this.handleCancel(e)
    }
    this.el.addEventListener('cancel', this.cancelHandler)

    // Listen for backdrop clicks
    this.clickHandler = (e) => {
      this.handleBackdropClick(e)
    }
    this.el.addEventListener('click', this.clickHandler)
  },

  openDialog () {
    try {
      this.el.showModal() // Opens as modal (with backdrop, focus trap)
    } catch (err) {
      console.error('[Dialog] Failed to open dialog:', this.el.id, err)
    }
  },

  closeDialog () {
    if (this.el.open) {
      // Flag this close as programmatic because the server initiated it.
      this.closingProgrammatically = true
      this.el.close()
      // Reset flag after a tick
      clearTimeout(this.closeResetTimer)
      this.closeResetTimer = setTimeout(() => {
        this.closingProgrammatically = false
      }, 0)
    }
  },

  handleClose () {
    // Dialog was closed (programmatically or by form method="dialog")
    // Only notify server if this wasn't a programmatic close initiated by server state
    if (!this.closingProgrammatically) {
      this.notifyServer()
    }
  },

  handleCancel () {
    // User pressed Escape key
    // Allow default behavior (closes dialog), then notify server
    // Flag this close as programmatic so handleClose doesn't double-notify.
    this.closingProgrammatically = true
    clearTimeout(this.cancelTimer)
    this.cancelTimer = setTimeout(() => {
      this.notifyServer()
      this.closingProgrammatically = false
    }, 0)
  },

  handleBackdropClick (e) {
    // Check if click was on the dialog backdrop (not content)
    const rect = this.el.getBoundingClientRect()
    const isInDialog = (
      rect.top <= e.clientY &&
      e.clientY <= rect.top + rect.height &&
      rect.left <= e.clientX &&
      e.clientX <= rect.left + rect.width
    )

    if (!isInDialog) {
      // Click was on backdrop - close directly and let handleClose notify server
      // Don't mark as programmatic - we want handleClose to notify the server
      this.el.close()
    }
  },

  notifyServer () {
    if (this.dialogDisposed) {return}

    // Push close event to LiveView if configured
    const closeEvent = this.el.dataset.onClose
    if (!closeEvent) {return}

    const reportFailure = (error) => {
      // A close notification that outlives this dialog no longer has a UI to
      // update. Unexpected failures still matter even after teardown.
      if (this.dialogDisposed && [
        'timeout',
        'no connection',
        'unable to push hook event. LiveView not connected',
        'failed with reason: {"reason":"unmatched topic"}'
      ].includes(error?.message)) {return}

      // Keep real action failures visible, with the context missing from an
      // asynchronous Phoenix rejection. No form values or payload are sent.
      captureException(error, {
        tags: { liveview_hook: 'Dialog', liveview_event: closeEvent }
      })
    }

    try {
      const pushed = this.pushEvent(closeEvent, {})
      if (pushed && typeof pushed.catch === 'function') {pushed.catch(reportFailure)}
    } catch (error) {
      reportFailure(error)
    }
  },

  destroyed () {
    this.dialogDisposed = true
    clearTimeout(this.cancelTimer)
    clearTimeout(this.closeResetTimer)
    // Clean up event listeners
    if (this.closeHandler) {
      this.el.removeEventListener('close', this.closeHandler)
    }
    if (this.cancelHandler) {
      this.el.removeEventListener('cancel', this.cancelHandler)
    }
    if (this.clickHandler) {
      this.el.removeEventListener('click', this.clickHandler)
    }
  }
}
